<script setup lang="ts">
import type {
	NavigationTab,
	RegionTab,
	TabNeighborhoodClickedPayload
} from './types';
import type { HTMLAttributes } from 'vue';

import { cn } from '@/utils';

const props = defineProps<{
	tabs: NavigationTab[];
	class?: HTMLAttributes['class'];
}>();

const activeTab = defineModel<RegionTab>('activeTab');
const tabsContainer = useTemplateRef<HTMLDivElement>('tabsContainer');
const canScrollLeft = ref(false);
const canScrollRight = ref(false);

const tabNeighborhoodClickedEventBus =
	useEventBus<TabNeighborhoodClickedPayload>('tab-neighborhood-clicked');

function handleTabClick(
	tab: NavigationTab,
	{ neighborhood_name, tab_position }: TabNeighborhoodClickedPayload
) {
	activeTab.value = {
		region: tab.id,
		state: tab.state,
		city: tab.city
	};

	tabNeighborhoodClickedEventBus.emit({
		tab_position,
		neighborhood_name
	});
}

// Use requestAnimationFrame to batch DOM reads and avoid forced reflow
// SSR-safe: requestAnimationFrame only runs on client
let scrollUpdatePending = false;

function updateScrollButtons() {
	if (!tabsContainer.value || scrollUpdatePending) return;

	// SSR guard: requestAnimationFrame is only available on client
	if (!import.meta.client) {
		// Fallback for SSR: read values directly (won't cause issues on server)
		const { scrollLeft, clientWidth, scrollWidth } = tabsContainer.value;
		canScrollLeft.value = scrollLeft > 0;
		canScrollRight.value =
			scrollLeft < scrollWidth - clientWidth && clientWidth > scrollLeft;
		return;
	}

	scrollUpdatePending = true;

	requestAnimationFrame(() => {
		scrollUpdatePending = false;
		if (!tabsContainer.value) return;

		// Batch all DOM reads together
		const { scrollLeft, clientWidth, scrollWidth } = tabsContainer.value;

		canScrollLeft.value = scrollLeft > 0;
		canScrollRight.value =
			scrollLeft < scrollWidth - clientWidth && clientWidth > scrollLeft;
	});
}

function scrollTabs(direction: 'left' | 'right') {
	if (!tabsContainer.value) return;

	const scrollAmount = 200;
	const targetScroll =
		tabsContainer.value.scrollLeft +
		(direction === 'left' ? -scrollAmount : scrollAmount);

	tabsContainer.value.scrollTo({
		left: targetScroll,
		behavior: 'smooth'
	});
}

let resizeObserver: ResizeObserver | null = null;

watch(tabsContainer, newValue => {
	// Cleanup previous observer if it exists
	if (resizeObserver) {
		resizeObserver.disconnect();
		resizeObserver = null;
	}

	if (newValue) {
		updateScrollButtons();

		resizeObserver = new ResizeObserver(updateScrollButtons);
		resizeObserver.observe(newValue);
	}
});

onUnmounted(() => {
	if (resizeObserver) {
		resizeObserver.disconnect();
		resizeObserver = null;
	}
});
</script>

<template>
	<div :class="cn('relative flex w-full items-center', props.class)">
		<Button
			v-if="canScrollLeft"
			variant="secondary"
			class="absolute left-4 top-1 z-10 hidden size-9 -translate-x-4 items-center justify-center border-none bg-secondary px-0 transition-opacity hover:bg-secondary-dark-1 lg:flex"
			@click="scrollTabs('left')"
		>
			<IconMaterialSymbolsLightChevronLeft
				class="min-h-5 min-w-5 text-primary"
			/>
		</Button>

		<div
			ref="tabsContainer"
			class="no-scrollbar flex w-full justify-start gap-5 overflow-x-auto scroll-smooth md:items-center md:px-0 md:pr-5 lg:gap-7"
			@scroll="updateScrollButtons"
		>
			<Button
				v-for="(tab, index) in tabs"
				:key="tab.id"
				:variant="tab.active ? 'primary' : 'secondary'"
				class="flex w-full select-none items-center gap-4 border-0 border-b-2 border-transparent! bg-none px-0! pt-2 font-matter-sq text-lg font-normal ring-0! md:py-2"
				:class="
					tab.active
						? 'border-primary! bg-white! text-primary'
						: 'border-white-dark-4 bg-white text-primary-light-4 hover:border-primary'
				"
				@click="
					handleTabClick(tab, {
						neighborhood_name: tab.label,
						tab_position: index + 1
					})
				"
			>
				{{ tab.label }}
			</Button>
		</div>

		<Button
			v-if="canScrollRight"
			variant="secondary"
			class="absolute right-[-16px] top-1 z-10 hidden size-9! -translate-x-4 items-center justify-center border-none bg-secondary px-0 transition-opacity hover:bg-secondary-dark-1 lg:flex"
			@click="scrollTabs('right')"
		>
			<IconMaterialSymbolsLightChevronRight
				class="min-h-5 min-w-5 text-primary"
			/>
		</Button>
	</div>
</template>

<!-- eslint-disable-next-line custom-rules/no-style-tag -->
<style scoped>
.no-scrollbar {
	-ms-overflow-style: none;
	scrollbar-width: none;
}
.no-scrollbar::-webkit-scrollbar {
	display: none;
}
</style>
